<template>
  <b-modal
    ref="quickViewModal"
    :title="modalTitle"
    size="xl"
    hide-footer
    scrollable
    @hidden="onHidden"
  >
    <div v-if="onLoading" class="text-center py-5">
      <b-spinner variant="primary" label="Đang tải"></b-spinner>
    </div>

    <div v-else-if="errorMessage">
      <b-alert show variant="danger">{{ errorMessage }}</b-alert>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" size="sm" class="mr-2" @click="openDetailPage">
          Mở trang chi tiết
        </b-button>
        <b-button variant="primary" size="sm" @click="fetchPR">
          <i class="fas fa-redo"></i> Thử lại
        </b-button>
      </div>
    </div>

    <div v-else class="pr-quick-view">
      <!-- Thông tin chung -->
      <h5 class="pr-qv-title">Thông tin chung</h5>
      <div class="pr-qv-grid mb-4">
        <div class="pr-qv-item">
          <div class="pr-qv-label">Mã phiếu</div>
          <div class="pr-qv-value"><strong>{{ pr.code || '—' }}</strong></div>
        </div>
        <div class="pr-qv-item">
          <div class="pr-qv-label">Trạng thái</div>
          <div class="pr-qv-value">
            <b-badge :variant="prStatusVariant(pr.status)">{{ prStatusName(pr.status) }}</b-badge>
          </div>
        </div>
        <div class="pr-qv-item">
          <div class="pr-qv-label">Kỳ ngân sách</div>
          <div class="pr-qv-value">{{ formatPeriod(pr.period) || '—' }}</div>
        </div>
        <div class="pr-qv-item">
          <div class="pr-qv-label">Tổng tiền đề nghị</div>
          <div class="pr-qv-value">
            <strong class="text-primary">{{ convertPrice(pr.totalAmount || pr.requestedAmount) }}</strong>
          </div>
        </div>
        <div class="pr-qv-item">
          <div class="pr-qv-label">Doanh nghiệp</div>
          <div class="pr-qv-value">{{ pr.companyName || pr.companyId || '—' }}</div>
        </div>
        <div class="pr-qv-item">
          <div class="pr-qv-label">Phòng ban</div>
          <div class="pr-qv-value">{{ pr.departmentName || pr.departmentId || '—' }}</div>
        </div>
        <div class="pr-qv-item">
          <div class="pr-qv-label">Người tạo</div>
          <div class="pr-qv-value">{{ pr.createdByName || pr.createdBy || '—' }}</div>
        </div>
        <div class="pr-qv-item">
          <div class="pr-qv-label">Ngày tạo</div>
          <div class="pr-qv-value">{{ formatDateTime(pr.createdAt) }}</div>
        </div>
      </div>

      <div class="pr-qv-block">
        <div class="pr-qv-label mb-1">Diễn giải</div>
        <div class="pr-qv-desc">{{ pr.description || '—' }}</div>
      </div>

      <!-- Tóm tắt duyệt -->
      <div class="pr-qv-block">
        <PurchaseRequestApprovalSummary :requestId="pr.id" :approvers="pr.approvers || []" />
      </div>

      <!-- Lý do từ chối / hủy -->
      <div v-if="pr.rejectionNote" class="pr-qv-block">
        <strong>Lý do từ chối/hủy:</strong> {{ pr.rejectionNote }}
      </div>

      <!-- Tệp đính kèm -->
      <div class="pr-qv-block">
        <div class="pr-qv-label mb-1">Tệp đính kèm</div>
        <FileUploadManager
          v-if="pr.id"
          :entity-id="pr.id"
          entity="purchase-requests"
          :disabled="true"
        />
      </div>

      <!-- Chi tiết khoản mục phí -->
      <div class="pr-qv-block">
        <h5 class="pr-qv-title">
          Chi tiết khoản mục phí
          <b-badge variant="info" class="ml-1">{{ (pr.lines || []).length }} dòng</b-badge>
        </h5>
        <div class="pr-qv-lines-scroll">
          <PurchaseRequestDetails
            :lines="pr.lines || []"
            :total-amount="pr.totalAmount"
            :requested-amount="pr.requestedAmount"
            :can-delete="false"
          />
        </div>
      </div>

      <!-- Thông tin phiếu chi HRM -->
      <div v-if="pr.hrmFinanceExternalCode" class="pr-qv-block">
        <h5 class="pr-qv-title">Thông tin phiếu chi</h5>
        <div class="pr-qv-grid">
          <div class="pr-qv-item">
            <div class="pr-qv-label">Mã phiếu chi</div>
            <div class="pr-qv-value">{{ pr.hrmFinanceExternalCode || '--' }}</div>
          </div>
          <div class="pr-qv-item">
            <div class="pr-qv-label">ID phiếu chi</div>
            <div class="pr-qv-value">{{ pr.hrmFinanceExternalId || '--' }}</div>
          </div>
          <div class="pr-qv-item">
            <div class="pr-qv-label">Trạng thái phiếu chi</div>
            <div class="pr-qv-value">{{ hrmFinanceExternalStatusName(pr.hrmFinanceExternalStatus) }}</div>
          </div>
          <div class="pr-qv-item">
            <div class="pr-qv-label">Số tiền thực chi</div>
            <div class="pr-qv-value text-primary">
              {{ pr.actualAmount != null ? convertPrice(pr.actualAmount) : '--' }}
            </div>
          </div>
          <div class="pr-qv-item">
            <div class="pr-qv-label">Số tiền hoàn ngân sách</div>
            <div class="pr-qv-value text-success">
              {{ pr.refundAmount != null ? convertPrice(pr.refundAmount) : '--' }}
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button variant="primary" size="sm" @click="openDetailPage">
          Mở trang chi tiết đầy đủ
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { convertPrice } from '@/utils/common';
import { formatDate, CLIENT_DATE_TIME_NO_SECONDS } from '@/utils/date';
import { PR_STATUS_BADGE_VARIANT } from '@/utils/enum';
import { PR_STATUS_NAME } from '@/utils/enum-name';
import PurchaseRequestApprovalSummary from './PurchaseRequestApprovalSummary.vue';
import PurchaseRequestDetails from './PurchaseRequestDetails.vue';
import FileUploadManager from '@/view/modules/upload/FileUploadManager.vue';

export default {
  name: 'PurchaseRequestQuickViewModal',
  components: {
    PurchaseRequestApprovalSummary,
    PurchaseRequestDetails,
    FileUploadManager,
  },
  data() {
    return {
      prId: null,
      pr: {},
      onLoading: false,
      errorMessage: '',
      // Tăng mỗi lần mở/đóng để bỏ qua response của phiếu đã đóng hoặc đã chuyển
      requestSeq: 0,
    };
  },
  computed: {
    modalTitle() {
      return this.pr.code
        ? `Chi tiết phiếu đề nghị mua hàng ${this.pr.code}`
        : 'Chi tiết phiếu đề nghị mua hàng';
    },
  },
  methods: {
    convertPrice,
    open(id) {
      this.prId = id;
      this.pr = {};
      this.errorMessage = '';
      this.$refs.quickViewModal.show();
      this.fetchPR();
    },
    onHidden() {
      this.requestSeq += 1;
      this.onLoading = false;
    },
    fetchPR() {
      if (!this.prId) return;
      this.requestSeq += 1;
      const seq = this.requestSeq;
      this.onLoading = true;
      this.errorMessage = '';
      ApiService.get(`v2/purchase-requests/${this.prId}`)
        .then(({ data }) => {
          if (seq !== this.requestSeq) return;
          if (data.status === 200) {
            this.pr = data.data || {};
          } else {
            this.errorMessage = data.message || 'Không tải được chi tiết phiếu.';
          }
          this.onLoading = false;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.errorMessage =
            error.response?.data?.message ??
            error.message ??
            'Không tải được chi tiết phiếu.';
          this.onLoading = false;
        });
    },
    openDetailPage() {
      const id = this.prId;
      this.$refs.quickViewModal.hide();
      this.$router.push({
        name: 'detail-purchase-request',
        query: { id, from: this.$route.fullPath },
      });
    },
    formatPeriod(val) {
      return val ? formatDate(val, 'MM/YYYY') : '';
    },
    formatDateTime(val) {
      return val ? formatDate(val, CLIENT_DATE_TIME_NO_SECONDS) : '';
    },
    prStatusVariant(val) {
      return PR_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    prStatusName(val) {
      return PR_STATUS_NAME[val] || val;
    },
    hrmFinanceExternalStatusName(val) {
      const map = { 1: 'Chờ duyệt', 2: 'Đã duyệt', 3: 'Không duyệt', 4: 'Đang duyệt', 5: 'Nháp' };
      return map[Number(val)] || '--';
    },
  },
};
</script>

<style scoped>
.pr-quick-view {
  font-size: 14px;
  color: #3f4254;
}
.pr-qv-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
}
.pr-qv-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 24px;
  row-gap: 14px;
}
.pr-qv-item {
  min-width: 0;
}
.pr-qv-label {
  font-weight: 500;
  font-size: 13px;
  color: #8c8ea3;
}
.pr-qv-value {
  margin-top: 2px;
  font-size: 14px;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.pr-qv-block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f1f5;
}
.pr-qv-desc {
  width: 100%;
  padding: 8px 12px;
  background-color: #f7f8fa;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-height: 160px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.pr-qv-lines-scroll {
  overflow-x: auto;
}
</style>
